import React, { Component } from 'react';
import { Row, Col, Container, Button, Spinner, CardTitle, Alert } from 'reactstrap';
import Chat from '../Chat/Chat';
import { API, graphqlOperation } from 'aws-amplify';
import { conversationsByUserID, messagesByChannelID } from '../../graphql/queries';
import {
  onCreateByChannelID,
  onDeleteConversationForUser,
  onNewConversationForUser,
  onUpdateConversationByChannelID,
} from '../../graphql/subscriptions';
import { updateMessage } from '../../graphql/mutations';
import { FaDotCircle } from 'react-icons/fa';
import { BsFillChatDotsFill } from 'react-icons/bs';
import { CgClose } from 'react-icons/cg';
import Loading from '../Loading';
import './ChatHub.css';

export default class ChatHub extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: null,
      minimized: false,
      chats: {}, // Key: channelId, value: { [messages], jobTitle, otherUsername, isAccepted, hasUnreadMessages, subscription, updateConversationSubscription }
      chatListOrder: [], // Keeps order of chat list. Elements are channelIds
      conversationsSubscription: null,
      hasNoConversations: false, // should be false until otherwise confirmed
      user: null,
      isMobile: false,
      shouldRenderAlert: false,
      hasAlertBeenRendered: false,
    };
  }

  componentDidMount = async () => {
    const { chats } = this.state;
    const currentUserId = this.props.user.attributes.sub;

    const isMobile = this.checkIfMobileBrowser();

    console.log('isMobile', isMobile);

    /* get proposalIds, job title, and other-user's username for each job/proposal belonging to user */
    // fetch Conversations
    const conversations = await this.getConversationsForUser(currentUserId);

    const hasNoConversations = conversations.length === 0;

    // subscribe to Conversations
    const conversationsSubscription = await this.subscribeToConversations(currentUserId);

    // note: channelIds are proposalIds
    let channelIds = [];

    for (let conversation of conversations) {
      const { channelID, jobTitle, otherUsername, isAccepted } = conversation;

      // create the object
      chats[channelID] = {
        messages: [],
        subscription: null,
        hasUnreadMessages: false,
        jobTitle,
        otherUsername,
        isAccepted,
      };

      // fetch initial messages for all open chat channels for user
      chats[channelID].messages = await this.getMessagesForChatChannel(channelID);

      // subscribe to all open chat channels for user
      chats[channelID].subscription = await this.subscribeToChatChannel(channelID);

      // subscribe to changes to all conversation records if they are not already accepted 
      // (we are currently only tracking when isAccepted goes false -> true)
      if (!isAccepted) {
        chats[channelID].updateConversationSubscription = await this.subscribeToConversationRecordChanges(channelID);
      }

      // if latest message is unread, then update hasUnreadMessages
      const lastMessageIndex = chats[channelID].messages.length - 1;
      const latestMessage = chats[channelID].messages[lastMessageIndex];
      if (latestMessage && !latestMessage.read && latestMessage.author !== this.props.user.attributes.sub) {
        chats[channelID].hasUnreadMessages = true;
      }

      channelIds.push(channelID);
    }

    // sort list of chats by latest message ts
    channelIds = this.sortByLatestMessageTs(channelIds);

    this.setState({
      chats,
      activeTab: channelIds[0],
      chatListOrder: channelIds,
      conversationsSubscription,
      hasNoConversations,
      isMobile,
    });
  };

  componentWillUnmount = async () => {
    const { chats, conversationsSubscription } = this.state;

    // unsubscribe from all chat channels
    for (let channelId in chats) {
      const subscription = chats[channelId].subscription;
      const updateConversationSubscription = chats[channelId].updateConversationSubscription;
      subscription.unsubscribe();
      if (updateConversationSubscription) {
        updateConversationSubscription.unsubscribe();
      }
    }
    if (conversationsSubscription) {
      conversationsSubscription.create.unsubscribe();
      conversationsSubscription.delete.unsubscribe();
    }

  };

  checkIfMobileBrowser = () => {
    let isMobile = false;
    // eslint-disable-next-line no-useless-escape
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) isMobile = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return isMobile;
  }

  getConversationsForUser = async (userId) => {
    const getConversationsRes = await API.graphql(
      graphqlOperation(conversationsByUserID, {
        userID: userId,
      }),
    );
    const items = getConversationsRes.data?.conversationsByUserID?.items;
    return items ? items : [];
  };

  getMessagesForChatChannel = async (channelId) => {
    const getMessagesRes = await API.graphql(
      graphqlOperation(messagesByChannelID, {
        channelID: channelId,
        sortDirection: 'ASC',
      }),
    );
    const items = getMessagesRes?.data?.messagesByChannelID?.items;
    return items ? items : [];
  };

  subscribeToChatChannel = async (channelId) => {
    const subscription = await API.graphql({
      query: onCreateByChannelID,
      variables: {
        channelID: channelId,
      },
    }).subscribe({
      next: (event) => {
        let { chats, chatListOrder, isMobile } = this.state;

        if (isMobile) {
          this.setState({ shouldRenderAlert: true });
        }

        const currentUser = this.props.user.attributes.sub;
        const newMessage = event.value.data.onCreateByChannelID;
        chats[channelId].messages.push(newMessage);
        chats[channelId].latestMessageTs = newMessage.createdAt;

        if (newMessage.author !== currentUser) {
          chats[channelId].hasUnreadMessages = true;
        }
        // if message isnt from the current user, then we have a new unread message
        chats[channelId].hasUnreadMessages = newMessage.author !== currentUser ? true : false;

        // move chat to top of chat list
        chatListOrder.splice(chatListOrder.indexOf(channelId), 1);
        chatListOrder.unshift(channelId);

        this.setState({
          chats,
          chatListOrder,
        });
      },
      error: (error) => {
        console.log('couldnt update messages');
        console.log(error);
      },
    });

    return subscription;
  };

  subscribeToConversations = async (userId) => {
    // subscription for new conversations
    const createConversationSubscription = await API.graphql({
      query: onNewConversationForUser,
      variables: {
        userID: userId,
      },
    }).subscribe({
      next: async (event) => {
        // new conversation was created
        let { activeTab, chats, chatListOrder, hasNoConversations } = this.state;
        const newConversation = event.value.data.onNewConversationForUser;
        const { channelID, jobTitle, otherUsername } = newConversation;

        // if user previously had no conversations, update state variable and set activeTab to render new chat
        if (hasNoConversations) {
          hasNoConversations = false;
          activeTab = channelID;
        }

        // get messages for the new conversation (should just be one)
        const messagesForNewChatChannel = await this.getMessagesForChatChannel(channelID);

        // subscribe to messages from new conversation
        const subscriptionToNewChatChannel = await this.subscribeToChatChannel(channelID);

        // add new conversation to chats object (should not exist yet)
        chats[channelID] = {
          messages: messagesForNewChatChannel,
          subscription: subscriptionToNewChatChannel,
          hasUnreadMessages: true,
          jobTitle,
          otherUsername,
        };

        // add new channelId to top of chatListOrder
        chatListOrder = [channelID, ...chatListOrder];

        // update state
        this.setState({
          activeTab,
          chats,
          chatListOrder,
          hasNoConversations,
        });
      },
      error: (error) => {
        console.log('couldnt update conversations');
        console.log(error);
      },
    });

    // subscription for deleting conversations
    const deleteConversationSubscription = await API.graphql({
      query: onDeleteConversationForUser,
      variables: {
        userID: userId,
      },
    }).subscribe({
      next: (event) => {
        let { activeTab, chats, chatListOrder } = this.state;
        const deletedConversation = event.value.data.onDeleteConversationForUser;
        const { channelID } = deletedConversation;

        // remove conversation from chatListOrder
        const indexOfDeletedChat = chatListOrder.indexOf(channelID);
        if (indexOfDeletedChat < 0) {
          console.log('couldnt find index of deleted chat');
          return; // TODO: throw an error here?
        }
        chatListOrder.splice(indexOfDeletedChat, 1);

        // unsubscribe from chat channel subscription
        chats[channelID].subscription.unsubscribe();

        // delete chats[channelId]
        delete chats[channelID];

        if (activeTab === channelID) {
          activeTab = chatListOrder[0];
        }

        this.setState({
          activeTab,
          chats,
          chatListOrder,
        });
      },
      error: (error) => {
        console.log('couldnt delete chat');
        console.log(error);
      },
    });

    const subscription = {
      create: createConversationSubscription,
      delete: deleteConversationSubscription,
    };

    return subscription;
  };

  subscribeToConversationRecordChanges = async (channelId) => {
    const updateConversationSubscription = await API.graphql({
      query: onUpdateConversationByChannelID,
      variables: {
        channelID: channelId,
      },
    }).subscribe({ 
      next: (event) => {
        let { chats } = this.state;
        const updatedConversation = event.value.data.onUpdateConversationByChannelID;
        console.log(`This conversation's proposal was accepted ${channelId}. Enabling chat for Freelancer`);
        console.log("event:", event);
        // update fields (currently there is only a use case for subscribing to changes to isAccepted)
        chats[channelId].isAccepted = updatedConversation.isAccepted;

        this.setState({
          chats,
        });
      },
      error: (error) => {
        console.log('couldnt update conversation record');
        console.log(error);
      },
    });

    return updateConversationSubscription;
  }


  readMessagesForChatChannel = async (channelId) => {
    const { chats } = this.state;
    const { messages } = chats[channelId];
    const currentUserId = this.props.user.attributes.sub;

    const readMessageOperations = [];
    const unreadMessageIndices = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      // if message belongs to other user and is read, we are done
      if (messages[i].author !== currentUserId && messages[i].read) {
        break;
      }
      // if message belongs to other user and is unread, add it to the array to be handled later
      if (messages[i].author !== currentUserId && !messages[i].read) {
        unreadMessageIndices.push(i);
        readMessageOperations.push(
          await API.graphql({
            query: updateMessage,
            variables: {
              input: {
                id: messages[i].id,
                read: true,
              },
            },
          }),
        );
      }
    }

    try {
      // update messages in db
      await Promise.all(readMessageOperations);
      // if successful, update messages locally
      for (let i of unreadMessageIndices) {
        messages[i].read = true;
      }

      // update messages in state to be 'read'
      chats[channelId].messages = messages;
      chats[channelId].hasUnreadMessages = false;

      this.setState({ chats });
    } catch (error) {
      console.log('couldnt read messages');
      console.log(error);
    }
  };

  toggle = async (tab) => {
    const { activeTab, chats } = this.state;

    // read the messages for the previous tab (unless previous tab was deleted)
    if (activeTab && chats[activeTab]) {
      this.readMessagesForChatChannel(activeTab);
    }

    if (activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  sortByLatestMessageTs = (channelIds) => {
    const { chats } = this.state;
    /*
			if returns (-) then a comes before b
			if returns (+) then b comes before a
			if returns (0) then no changes between the two values
	 	*/
    let sortedChannelIds = channelIds.sort((a, b) => {
      const aMessagesLength = chats[a].messages.length;
      const bMessagesLength = chats[b].messages.length;

      // if no messages
      if (aMessagesLength === 0 && bMessagesLength === 0) {
        return 0; // no preference between the two (TODO: maybe compare by channelId timestamp in this case?)
      }
      // if only b has messages
      if (aMessagesLength === 0) {
        return 1; // b comes before a
      }
      // if only a has messages
      if (bMessagesLength === 0) {
        return -1; // a comes before b
      }
      // otherwise whichever has the latest timestamp on the last message should come first
      const dateA = new Date(chats[a].messages[aMessagesLength - 1].createdAt);
      const dateB = new Date(chats[b].messages[bMessagesLength - 1].createdAt);

      return dateB.getTime() - dateA.getTime(); // if b > a returns (+), if b < a returns (-)
    });

    return sortedChannelIds;
  };

  renderChatTabs = () => {
    const { chats, chatListOrder, activeTab } = this.state;

    // render tabs for all channelIds
    let chatTabList = [];
    for (let channelId of chatListOrder) {
      const { jobTitle, otherUsername, hasUnreadMessages } = chats[channelId];
      chatTabList.push(
        <Row
          className={`chat-list-item ${channelId === activeTab ? 'chat-list-item-active' : ''}`}
          key={channelId}
          onClick={() => this.toggle(channelId)}>
          <CardTitle tag="h6">
            {otherUsername}
            {hasUnreadMessages && (
              <div className={'chat-list-unread-icon'}>
                <FaDotCircle id="myicon" size={12} />
              </div>
            )}
          </CardTitle>
          <p className="chat-list-item-job-title">{jobTitle}</p>
        </Row>,
      );
    }

    return chatTabList;
  };

  renderActiveChat = () => {
    const { activeTab, chats } = this.state;

    if (!chats[activeTab]) {
      return <Loading />;
    }
    let { messages, jobTitle, otherUsername, isAccepted, hasUnreadMessages } = chats[activeTab];
    return (
      <Chat
        channelId={activeTab}
        user={this.props.user}
        messages={messages}
        jobTitle={jobTitle}
        otherUsername={otherUsername}
        hasUnreadMessages={hasUnreadMessages}
        isAccepted={isAccepted}
        readMessages={() => this.readMessagesForChatChannel(activeTab)}
      />
    );
  };

  render() {
    const { minimized, hasNoConversations, conversationsSubscription, isMobile, shouldRenderAlert, hasAlertBeenRendered } = this.state;

    // we are currently disabling chat for mobile devices. give an alert to the mobile user to visit our desktop site
    if (isMobile)  {
      if (shouldRenderAlert && !hasAlertBeenRendered) {
        console.log('rendering Alert');
        return (
          <div className="fixed-bottom">
            <Alert className="mobile-chat-alert" color="primary"> 
                <div className="mobile-chat-alert-body">You are getting messaged! Sign in on our desktop site to see your active chats!</div>
                <div style={{ float: "right" }}>
                  <button className="mobile-chat-alert-button" onClick={() => {this.setState({ shouldRenderAlert: false, hasAlertBeenRendered: true });}}>
                    <CgClose className="close-alert-icon" size={28} />
                  </button>
                </div>
            </Alert>
          </div>
        );
      }
      return null;
    }

    // if still loading
    if (!conversationsSubscription) {
      return (
        <Button className="maximize-btn" disabled onClick={() => this.setState({ minimized: false })}>
          <Spinner animation="spin" variant="light">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Button>
      );
    }

    if (minimized) {
      return (
        <Button className="maximize-btn" onClick={() => this.setState({ minimized: false })}>
          <BsFillChatDotsFill size={28} />
        </Button>
      );
    }

    // if user has no conversations
    if (hasNoConversations) {
      return (
        <Container className="chat-hub-window">
          <Container fluid>
            <Button className="minimize-btn" onClick={() => this.setState({ minimized: true })}>
              <CgClose className="minimize-icon" size={28} />
            </Button>
            <div className="chat-hub-empty-message">
              <p>
                <strong>You have no conversations!</strong>
              </p>
              <p>Wait for proposals on your jobs or create a proposal on someone elses job to get chatting!</p>
            </div>
          </Container>
        </Container>
      );
    }

    return (
      <Container className="chat-hub-window">
        <Container fluid>
          <Row className="chat-hub-row">
            <Col sm="4" className="chat-list-col">
              <Row className="chat-list-header">
								<Button
									className="minimize-btn"
									onClick={() => this.setState({ minimized: true })}>
									<CgClose className="minimize-icon" size={28} />
								</Button>
								<div className="chat-list-header-title">
									My Open Chats
								</div>
              </Row>
              <div className="chat-list-container-wrapper">
                <div className="class-list-container">
                  <div className="chat-list-tabs">{this.renderChatTabs()}</div>
                </div>
              </div>
            </Col>
            <Col sm="8" className="chat-window">
              {this.renderActiveChat()}
            </Col>
          </Row>
        </Container>
      </Container>
    );
  }
}
